import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";
import COLORS from "../colors";
import { parse, zoneLabelsFrom } from "../util/warehouseSvg";
import { overlappingZones } from "../util/warehouseGeometry";

// Per-zone flags, keyed by zone label (the zone's `warehouse:label`
// identity, parsed out of the SVG). `binCount` is the highest valid bin
// number, or null for uncapped (ItemLocation currently accepts 1–999).
export const WAREHOUSE_ZONE_FIELDS = {
  hasBins: false,
  binCount: null,
};
deepFreeze(WAREHOUSE_ZONE_FIELDS);

// Label size for new maps (and the fallback when a map doesn't say), in map
// units. The editor's grid defaults to 1 unit.
export const DEFAULT_LABEL_SIZE = 4;

// A warehouse name is the document id and the prefix of every
// `warehouse:zone[:bin]` location id, which readers split on ":", so it can't
// contain ":" (or "," or "/", which also break the id's other uses). "." and
// "..", and `__x__`, are reserved Firestore ids.
export const isValidWarehouseName = (name) => {
  const id = String(name ?? "").trim();
  return (
    Boolean(id) &&
    !/[/:,]/.test(id) &&
    !/^\.\.?$/.test(id) &&
    !/^__.*__$/.test(id)
  );
};

export const WAREHOUSE_MAP_FIELDS = {
  // Display name, e.g. "180EB". Also the document id and the warehouse
  // prefix used in `warehouse:zone` ItemLocation references.
  name: "",
  // Key into LOCATION_INFO (e.g. "westervillewh") linking this warehouse to a
  // physical location. Empty until an admin assigns one.
  locationId: "",
  // Sort order for pickers/maps; lower sorts first.
  order: 0,
  // The full SVG document — the only source of geometry. Zones are `g.zone`
  // groups; each zone's identity is the joined text of its <tspan>s (see
  // util/warehouseSvg).
  svg: "",
  // Font-size for every label, in map (viewBox) units.
  labelSize: 0,
  // Snap grid for the editor, in map units.
  gridSize: 1,
  // { [zoneLabel]: WAREHOUSE_ZONE_FIELDS } — bin flags for zones parsed from
  // the SVG.
  zones: {},
  // Whether this map is live: only active maps contribute their zones to the
  // receiving/put-away location pickers. Draft maps stay inactive.
  active: false,
  // The default warehouse map used for receiving/put-away. At most one map
  // should carry this; the `defaultWarehouse` getter prefers it (among active
  // maps).
  isDefault: false,
  archived: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(WAREHOUSE_MAP_FIELDS);

// Display statuses for the admin table/canvas status chip. Order of checks
// in `statusKey` below decides which applies.
export const WAREHOUSE_MAP_STATUSES = {
  default: {
    label: "Default receiving map",
    color: "positive",
    icon: "done",
  },
  active: { label: "Active", color: "positive", icon: "warehouse" },
  draft: { label: "Draft", color: "warning", icon: "edit" },
  archived: { label: "Archived", color: "grey", icon: "archive" },
};
deepFreeze(WAREHOUSE_MAP_STATUSES);

// Instance memo for `zoneLabels` — keyed by instance so frozen store
// records still get caching (see the getter's comment).
const zoneLabelsMemo = new WeakMap();
const overlapMemo = new WeakMap();

// Zone flags are keyed by label; each value is normalized against
// WAREHOUSE_ZONE_FIELDS and unknown keys are dropped.
function sanitizeZones(zones) {
  const result = {};
  Object.entries(zones && typeof zones === "object" ? zones : {}).forEach(
    ([label, flags]) => {
      result[label] = sanitize(flags, WAREHOUSE_ZONE_FIELDS);
    }
  );
  return result;
}

export default class WarehouseMap extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, WAREHOUSE_MAP_FIELDS);
    data.zones = sanitizeZones(data.zones);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: WarehouseMap.canBeViewedBy,
      create: WarehouseMap.canBeCreatedBy,
      edit: WarehouseMap.canBeEditedBy,
      delete: WarehouseMap.canBeDeletedBy,
    });
  }

  // Permissions - supers and the warehouse role manage maps; viewing uses the
  // same bar so the admin pages stay management-only (employees still read
  // the Firestore docs for receiving/put-away and location display).

  static canBeViewedBy(user) {
    return user.isSuper || user.hasRole("warehouse");
  }

  static canBeCreatedBy(user) {
    return user.isSuper || user.hasRole("warehouse");
  }

  static canBeEditedBy(user) {
    return user.isSuper || user.hasRole("warehouse");
  }

  static canBeDeletedBy(user) {
    return user.isSuper || user.hasRole("warehouse");
  }

  // Database

  get path() {
    return WarehouseMap.getPath(this.id);
  }

  static getPath(id) {
    if (id) {
      return `warehouseMaps/${id}`;
    }
    return "warehouseMaps";
  }

  static getStateID(path) {
    const match = path.match(/^warehouseMaps\/(.+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new WarehouseMap(snapshot.id, data);
  }

  // Editing

  get rules() {
    const isSet = (a) => a !== null && a !== undefined && a !== "";
    return {
      name: [
        (a) => isSet(a) || "required",
        (a) => !isSet(a) || isValidWarehouseName(a) || "invalidWarehouseName",
      ],
      svg: [
        () => this.svgError || true,
        // Overlapping zones are fine while drafting, but a live map must
        // not have them: a location would be ambiguous on the map.
        () =>
          !this.data.active ||
          !this.overlappingZoneLabels.length ||
          "zonesOverlap",
      ],
      labelSize: [(a) => a > 0 || "required"],
      gridSize: [(a) => a > 0 || "required"],
    };
  }

  // The SVG must parse and yield at least one zone with unique, non-empty
  // labels; labels may not contain ":" or "," (they form the
  // `warehouse:label` reference). Returns the i18n error key, or null when
  // valid.
  get svgError() {
    if (!this.data.svg) return "invalidMapSvg";
    let model;
    try {
      model = parse(this.data.svg);
    } catch {
      return "invalidMapSvg";
    }
    const labels = model.zones.map((zone) => zone.label);
    if (!labels.length) return "invalidMapSvg";
    if (labels.some((label) => !label || /[:,]/.test(label))) {
      return "invalidZoneLabel";
    }
    if (new Set(labels).size !== labels.length) {
      return "duplicateZoneLabel";
    }
    return null;
  }

  get isValid() {
    return this.validationErrors.length === 0;
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, WAREHOUSE_MAP_FIELDS)));
  }

  isEqual(map) {
    return Boolean(this.id === map.id && isEmpty(diff(this.data, map.data)));
  }

  // Display / derived

  // Zone labels parsed from the SVG, memoised on the svg string — the
  // editor buffer mutates data.svg, so the cache is keyed on the content.
  // The memo lives in a WeakMap, not on the instance: the store deep-freezes
  // its records (mutations/init.js SET), so `this._zoneLabels = …` throws on
  // any map that arrived through a listener.
  get zoneLabels() {
    const cached = zoneLabelsMemo.get(this);
    if (cached && cached.svg === this.data.svg) return cached.labels;
    let labels;
    try {
      labels = this.data.svg ? zoneLabelsFrom(this.data.svg) : [];
    } catch {
      labels = [];
    }
    zoneLabelsMemo.set(this, { svg: this.data.svg, labels });
    return labels;
  }

  // Labels of zones whose area overlaps another zone's, memoised on the svg
  // string like zoneLabels.
  get overlappingZoneLabels() {
    const cached = overlapMemo.get(this);
    if (cached && cached.svg === this.data.svg) return cached.labels;
    let labels;
    try {
      const zones = this.data.svg ? parse(this.data.svg).zones : [];
      labels = overlappingZones(zones).map((i) => zones[i].label);
    } catch {
      labels = [];
    }
    overlapMemo.set(this, { svg: this.data.svg, labels });
    return labels;
  }

  // Fully-qualified `warehouse:label` locations for zones flagged hasBins,
  // matching the warehouseLocationsWithBins getter's entry format.
  get binZoneLocations() {
    return this.zoneLabels
      .filter((label) => this.data.zones[label]?.hasBins)
      .map((label) => `${this.id}:${label}`);
  }

  hasBins(label) {
    return Boolean(this.data.zones[label]?.hasBins);
  }

  // Highest valid bin number for the zone, or null when uncapped.
  binCap(label) {
    return this.data.zones[label]?.binCount ?? null;
  }

  // Occupancy — derived client-side from loaded inventory. Note this counts
  // only loose inventory items (the `inventory` collection loaded into the
  // store), NOT sale-allocated goods physically staged in the zone; a
  // maintained occupancy index is required for a fully authoritative count.
  // Used to block deleting/renaming a zone that currently holds inventory.
  zoneItemCount(label) {
    const target = `${this.id}:${label}`;
    const main = store.state?.main || {};
    let count = 0;
    const scan = (items) => {
      Object.values(items || {}).forEach((item) => {
        // Skip archived inventory and fully-delivered sale items (their stock
        // is no longer physically in the zone). Inventory items have no
        // `fullyDelivered`, so that guard is a no-op for them.
        if (!item || item.data.archived || item.fullyDelivered) {
          return;
        }
        item.itemLocation.forEach((location) => {
          // Compare `warehouse:zone`: a record that kept its bin in the id
          // (`180EB:S:12`) is still in zone S (see util/warehouseAudit).
          const zone = String(location.data.id)
            .split(":")
            .slice(0, 2)
            .join(":");
          if (zone === target && location.data.quantity > 0) {
            count += location.data.quantity;
          }
        });
      });
    };
    // Count both loose inventory and sale-allocated (holding) items so the
    // occupancy guards reflect everything physically in the zone.
    scan(main.inventoryItems);
    scan(main.saleItems);
    return count;
  }

  isZoneOccupied(label) {
    return this.zoneItemCount(label) > 0;
  }

  // Zones in this map that currently hold inventory: [{ label, count }].
  // Used to guard deleting/archiving a warehouse that still has stock.
  occupiedZones() {
    return this.zoneLabels
      .map((label) => ({
        label,
        count: this.zoneItemCount(label),
      }))
      .filter((zone) => zone.count > 0);
  }

  // Given an { oldLabel: newLabel } rename map from the editor, return the
  // renames whose ORIGINAL label currently holds inventory. A zone's label is
  // its `warehouse:label` reference, so renaming an occupied zone orphans the
  // existing item locations — callers should confirm before saving.
  renamedOccupiedZones(renames) {
    return Object.entries(renames || {})
      .filter(([from, to]) => from !== to)
      .map(([from, to]) => ({
        from,
        to,
        count: this.zoneItemCount(from),
      }))
      .filter((rename) => rename.count > 0);
  }

  // Display status: archived > default > active > draft.
  get statusKey() {
    if (this.data.archived) return "archived";
    if (this.data.isDefault && this.data.active) return "default";
    if (this.data.active) return "active";
    return "draft";
  }

  get status() {
    const type = WAREHOUSE_MAP_STATUSES[this.statusKey];
    return { ...type, ...COLORS[type.color] };
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  toString() {
    return this.data.name || this.id || "";
  }
}
